import React, { createContext, useContext, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useAuth } from './AuthContext';
import { whoami, WhoAmI, ApiError } from '../api';
import { COLORS } from '../pos/theme';

type Status =
  | { kind: 'checking' }
  | { kind: 'ok'; me: WhoAmI }
  | { kind: 'denied'; me: WhoAmI }
  | { kind: 'error'; message: string };

type Props = { children: React.ReactNode };

const WhoAmIContext = createContext<WhoAmI | null>(null);

export const useWhoAmI = (): WhoAmI => {
  const me = useContext(WhoAmIContext);
  if (!me) {
    throw new Error('useWhoAmI called outside the authorized subtree');
  }
  return me;
};

const centerSx = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: 2,
  px: 2,
  backgroundColor: COLORS.pageBg,
} as const;

/** Register access gate. Accepts `admin` (everyone who can already use
 *  web-admin) or `pos` (a till-only grant for cashiers). Mirrors
 *  `auth::PosPrincipal` server-side — keep the two in step. */
const PosGate = ({ children }: Props) => {
  const { idToken, signOut, user } = useAuth();
  const [status, setStatus] = useState<Status>({ kind: 'checking' });

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setStatus({ kind: 'checking' });
    whoami(idToken)
      .then((me) => {
        if (cancelled) return;
        if (me.roles.includes('admin') || me.roles.includes('pos')) {
          setStatus({ kind: 'ok', me });
        } else {
          setStatus({ kind: 'denied', me });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          // Firebase normally refreshes before a token is rejected, so
          // a 401 here means the email isn't in the user directory.
          setStatus({
            kind: 'denied',
            me: { email: user?.email || '(unknown)', roles: [] },
          });
        } else {
          setStatus({ kind: 'error', message: err?.message || 'unknown error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, user]);

  if (status.kind === 'checking') {
    return (
      <Box sx={centerSx}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (status.kind === 'denied') {
    return (
      <Box sx={centerSx}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.text }}>
          Not authorized
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.textMuted, maxWidth: 380 }}>
          This account can't use the register. Talk to the admin.
        </Typography>
        <Button variant="outlined" onClick={signOut}>
          Sign out
        </Button>
      </Box>
    );
  }
  if (status.kind === 'error') {
    return (
      <Box sx={centerSx}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.text }}>
          Backend unreachable
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.textMuted, maxWidth: 380 }}>
          {status.message}
        </Typography>
        <Button variant="outlined" onClick={signOut}>
          Sign out
        </Button>
      </Box>
    );
  }
  return (
    <WhoAmIContext.Provider value={status.me}>{children}</WhoAmIContext.Provider>
  );
};

export default PosGate;
